import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from '../../../utils/api';
import styles from './AcademicCyclesSection.module.css';

const API = process.env.REACT_APP_API_URL || '/api';
const LEVELS = [
  { key: 'bachillerato', label: 'Bachillerato' },
  { key: 'tsu', label: 'TSU' },
  { key: 'degree', label: 'Licenciaturas' },
  { key: 'masterDegree', label: 'Maestrías' },
  { key: 'speciality', label: 'Especialidades' },
];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const emptyCycles = () => LEVELS.map(({ key }) => ({
  educationalLevel: key,
  startDates: [{ month: '', day: '' }],
}));

const mergeCycles = (cycles = []) => LEVELS.map(({ key }) => {
  const stored = cycles.find((cycle) => cycle.educationalLevel === key);
  return {
    educationalLevel: key,
    startDates: stored?.startDates?.length
      ? stored.startDates.map(({ month, day }) => ({ month, day }))
      : [{ month: '', day: '' }],
  };
});

const formatDeadline = (value) => {
  if (!value) return 'Sin fecha calculada';
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Mexico_City',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
};

export default function AcademicCyclesSection() {
  const [cycles, setCycles] = useState(emptyCycles);
  const [deadlines, setDeadlines] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadCycles = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await fetch(`${API}/global-config/academic-cycles`, { cache: 'no-store' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || 'No se pudieron cargar los ciclos escolares.');
      setCycles(mergeCycles(body.data));
      setDeadlines(Object.fromEntries((body.data || []).map((cycle) => (
        [cycle.educationalLevel, cycle.nextRegistrationDeadline]
      ))));
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCycles(); }, [loadCycles]);

  const updateDate = (level, index, field, value) => {
    setCycles((current) => current.map((cycle) => (
      cycle.educationalLevel !== level
        ? cycle
        : {
          ...cycle,
          startDates: cycle.startDates.map((date, dateIndex) => (
            dateIndex === index ? { ...date, [field]: value } : date
          )),
        }
    )));
    setMessage({ type: '', text: '' });
  };

  const addDate = (level) => {
    setCycles((current) => current.map((cycle) => (
      cycle.educationalLevel === level
        ? { ...cycle, startDates: [...cycle.startDates, { month: '', day: '' }] }
        : cycle
    )));
  };

  const removeDate = (level, index) => {
    setCycles((current) => current.map((cycle) => (
      cycle.educationalLevel === level
        ? { ...cycle, startDates: cycle.startDates.filter((_, dateIndex) => dateIndex !== index) }
        : cycle
    )));
  };

  const saveCycles = async () => {
    const invalidLevel = cycles.find((cycle) => (
      cycle.startDates.length === 0
      || cycle.startDates.some(({ month, day }) => !month || !day)
    ));
    if (invalidLevel) {
      const label = LEVELS.find(({ key }) => key === invalidLevel.educationalLevel)?.label;
      setMessage({ type: 'error', text: `Completa al menos una fecha válida para ${label}.` });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await fetch(`${API}/global-config/academic-cycles`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          academicCycles: cycles.map((cycle) => ({
            ...cycle,
            startDates: cycle.startDates.map(({ month, day }) => ({
              month: Number(month), day: Number(day),
            })),
          })),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || 'No se pudieron guardar los ciclos escolares.');
      setCycles(mergeCycles(body.data));
      setDeadlines(Object.fromEntries((body.data || []).map((cycle) => (
        [cycle.educationalLevel, cycle.nextRegistrationDeadline]
      ))));
      setMessage({ type: 'success', text: 'Ciclos escolares guardados correctamente.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Ciclos escolares</h1>
          <p className={styles.description}>
            El mes programa el primer pago al día 1. El día exacto muestra la siguiente fecha límite de inscripción en la landing.
          </p>
        </div>
        <button type="button" className={styles.saveButton} onClick={saveCycles} disabled={loading || saving}>
          {saving ? 'Guardando...' : 'Guardar ciclos'}
        </button>
      </div>

      {loading ? <p className={styles.notice}>Cargando configuración...</p> : (
        <div className={styles.levels}>
          {LEVELS.map(({ key, label }) => {
            const cycle = cycles.find(({ educationalLevel }) => educationalLevel === key);
            return (
              <section key={key} className={styles.card}>
                <div className={styles.cardHeader}>
                  <div>
                    <h2 className={styles.levelTitle}>{label}</h2>
                    <p className={styles.deadline}>
                      Próxima fecha pública: {formatDeadline(deadlines[key])}
                    </p>
                  </div>
                  <button type="button" className={styles.addButton} onClick={() => addDate(key)}>
                    + Agregar fecha
                  </button>
                </div>

                <div className={styles.dateList}>
                  {cycle.startDates.map((date, index) => (
                    <div key={`${key}-${index}`} className={styles.dateRow}>
                      <label className={styles.field}>
                        Mes
                        <select
                          aria-label={`${label} mes ${index + 1}`}
                          value={date.month}
                          onChange={(event) => updateDate(key, index, 'month', event.target.value)}
                        >
                          <option value="">Selecciona</option>
                          {MONTHS.map((month, monthIndex) => (
                            <option key={month} value={monthIndex + 1}>{month}</option>
                          ))}
                        </select>
                      </label>
                      <label className={styles.field}>
                        Día
                        <input
                          aria-label={`${label} día ${index + 1}`}
                          type="number"
                          min="1"
                          max="31"
                          value={date.day}
                          onChange={(event) => updateDate(key, index, 'day', event.target.value)}
                        />
                      </label>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => removeDate(key, index)}
                        disabled={cycle.startDates.length === 1}
                        aria-label={`Eliminar fecha ${index + 1} de ${label}`}
                      >
                        Eliminar
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {message.text && (
        <p className={message.type === 'error' ? styles.error : styles.success}>{message.text}</p>
      )}
    </div>
  );
}
